import type { ProjectDefinition } from "../../../types";
import {
  type CellValue,
  type FuncionProjectSpec,
  NOMBRES,
  conditions,
  excelDate,
  seededRandom,
} from "../spec";

const SHEET = "MD Talento";
const TABLE = "Empleados";

const DEPTOS = ["Ventas", "Finanzas", "TI", "Operaciones", "Recursos Humanos"] as const;
const CIUDADES = ["Monterrey", "CDMX", "Guadalajara", "Puebla"] as const;
const PUESTOS: Record<(typeof DEPTOS)[number], string[]> = {
  Ventas: ["Ejecutivo de cuenta", "Gerente de ventas", "Analista comercial"],
  Finanzas: ["Analista financiero", "Contador", "Gerente de finanzas"],
  TI: ["Desarrollador", "Analista de datos", "Soporte técnico"],
  Operaciones: ["Coordinador de logística", "Supervisor", "Auxiliar de almacén"],
  "Recursos Humanos": ["Reclutador", "Analista de nómina", "Gerente de talento"],
};

const rnd = seededRandom(1212);
// ID, Nombre, Departamento, Puesto, Ciudad, Ingreso, Salario, Evaluación
const ROWS = NOMBRES.slice(0, 35).map((nombre, i) => {
  const depto = DEPTOS[i % DEPTOS.length];
  const puesto = rnd.pick(PUESTOS[depto]);
  const base = puesto.startsWith("Gerente") ? 60000 : puesto.startsWith("Auxiliar") ? 14000 : 22000;
  return [
    `E-${201 + i}`,
    nombre,
    depto,
    puesto,
    rnd.pick(CIUDADES),
    excelDate("2015-01-05") + rnd.int(0, 4000),
    base + rnd.round(0, 30000, 500),
    rnd.int(1, 5),
  ] as [string, string, string, string, string, number, number, number];
});

type Row = (typeof ROWS)[number];
const IN = {
  depto: "Ventas",
  ciudad: "Monterrey",
  ciudad2: "Guadalajara",
  salario: 50000,
  deptoVacio: "Legal",
  texto: "Analista",
};

const pick = (filter: (r: Row) => boolean): CellValue[][] => ROWS.filter(filter);

/** Expected result of each task, in order. */
const EXPECTED: CellValue[][][] = [
  pick((r) => r[2] === IN.depto),
  pick((r) => r[6] > IN.salario),
  [["Sin resultados"]],
  pick((r) => r[2] === IN.depto && r[4] === IN.ciudad),
  pick((r) => r[4] === IN.ciudad || r[4] === IN.ciudad2),
  ROWS.filter((r) => r[4] === IN.ciudad).map((r) => [r[1], r[2], r[3]]),
  pick((r) => r[3].toLowerCase().includes(IN.texto.toLowerCase())),
  pick((r) => r[2] === IN.depto && (r[7] >= 4 || r[6] > IN.salario)),
];

/** Each answer starts 3 rows below the previous result, so spills never collide. */
const ANCHORS: string[] = [];
EXPECTED.reduce((row, result) => {
  ANCHORS.push(`K${row}`);
  return row + result.length + 3;
}, 13);

const LABELS = [
  "Tarea 1 · Empleados del departamento",
  "Tarea 2 · Salario mayor al mínimo",
  "Tarea 3 · Departamento sin empleados",
  "Tarea 4 · Departamento y ciudad",
  "Tarea 5 · Una ciudad u otra",
  "Tarea 6 · Solo nombre, departamento y puesto",
  "Tarea 7 · Puesto que contiene el texto",
  "Tarea 8 · Talento destacado del departamento",
];
const FNS = [["FILTER"], ["FILTER"], ["FILTER"], ["FILTER"], ["FILTER"], ["FILTER"], ["FILTER", "SEARCH"], ["FILTER"]];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-filtrar",
  sheets: [
    {
      name: SHEET,
      title: "Buscador de talento",
      subtitle: "Base de empleados — cada resultado se derrama desde su celda amarilla",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["ID", "Nombre", "Departamento", "Puesto", "Ciudad", "Ingreso", "Salario", "Evaluación"],
          rows: ROWS,
          formats: { Ingreso: "dd/mm/yyyy", Salario: "$#,##0" },
        },
      ],
      inputs: [
        { address: "K4", label: "Departamento", value: IN.depto },
        { address: "K5", label: "Ciudad", value: IN.ciudad },
        { address: "K6", label: "Otra ciudad", value: IN.ciudad2 },
        { address: "K7", label: "Salario mínimo", value: IN.salario, format: "$#,##0" },
        { address: "K8", label: "Departamento a consultar", value: IN.deptoVacio },
        { address: "K9", label: "Texto en el puesto", value: IN.texto },
      ],
      answers: ANCHORS.map((address, i) => ({ address, label: LABELS[i], labelPosition: "above" as const })),
    },
  ],
  checks: Object.fromEntries(
    EXPECTED.map((expected, i) => [
      i + 1,
      { kind: "spill" as const, sheet: SHEET, address: ANCHORS[i], fns: FNS[i], expected },
    ])
  ),
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-filtrar",
  title: "FILTRAR: Buscador de talento",
  subtitle: "Una fórmula que devuelve todas las filas que cumplen",
  description:
    "Recursos Humanos te pide armar un buscador sobre la base de 35 empleados: por departamento, por salario, por ciudad, por puesto… y que se actualice solo cuando cambien los criterios.\n\nFILTRAR devuelve todas las filas que cumplen una condición y el resultado se \"derrama\" hacia abajo y a la derecha. Si hay algo estorbando, verás #¡DESBORDAMIENTO!.",
  topics: [
    "Filtrar filas completas con una condición",
    "Mensaje cuando no hay resultados",
    "Y con *, O con +",
    "Devolver solo algunas columnas",
    "Texto parcial con ESNUMERO(HALLAR(...))",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Empleados de un departamento",
      description:
        `En la hoja <strong>MD Talento</strong>, celda <strong>${ANCHORS[0]}</strong>: muestra todas las filas de <strong>Empleados</strong> cuyo <strong>Departamento</strong> sea el de <strong>K4</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[0])}.`,
        `FILTRAR(matriz, incluir): la matriz es la tabla completa y \"incluir\" es la condición fila por fila: ${F("Empleados[Departamento]=K4")}.`,
        `Escribe ${F("=FILTRAR(Empleados,Empleados[Departamento]=K4)")} y presiona ${F("Enter")}. El resultado ocupa varias filas y columnas.`,
      ],
      expectedResult: "Todos los empleados de Ventas",
      finalFormula: "=FILTRAR(Empleados,Empleados[Departamento]=K4)",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Salarios altos",
      description:
        `En <strong>${ANCHORS[1]}</strong>: muestra los empleados con <strong>Salario mayor</strong> al de <strong>K7</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[1])}.`,
        `Escribe ${F("=FILTRAR(Empleados,Empleados[Salario]>K7)")}.`,
        "Cambia K7 y mira cómo el resultado crece o se encoge solo.",
      ],
      expectedResult: "Empleados que ganan más de $50,000",
      finalFormula: "=FILTRAR(Empleados,Empleados[Salario]>K7)",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Cuando no hay resultados",
      description:
        `En <strong>${ANCHORS[2]}</strong>: filtra los empleados del departamento de <strong>K8</strong>. No existe ninguno, así que FILTRAR daría <strong>#¡CALC!</strong>: haz que muestre <strong>Sin resultados</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[2])}.`,
        "FILTRAR tiene un tercer argumento, si_vacío, que se muestra cuando ninguna fila cumple.",
        `Escribe ${F('=FILTRAR(Empleados,Empleados[Departamento]=K8,"Sin resultados")')}.`,
      ],
      expectedResult: "Sin resultados",
      finalFormula: '=FILTRAR(Empleados,Empleados[Departamento]=K8,"Sin resultados")',
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: Departamento Y ciudad",
      description: conditions(`En <strong>${ANCHORS[3]}</strong>: muestra los empleados que cumplan <strong>las dos</strong> condiciones:`, [
        "Departamento igual al de <strong>K4</strong>",
        "Ciudad igual a la de <strong>K5</strong>",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[3])}.`,
        "Dentro de FILTRAR no se usa Y(). Cada condición da VERDADERO/FALSO (1/0) por fila; al multiplicarlas, solo queda 1 donde las dos son verdaderas.",
        `Escribe ${F("=FILTRAR(Empleados,(Empleados[Departamento]=K4)*(Empleados[Ciudad]=K5))")}. Los paréntesis de cada condición son obligatorios.`,
      ],
      expectedResult: "Empleados de Ventas en Monterrey",
      finalFormula: "=FILTRAR(Empleados,(Empleados[Departamento]=K4)*(Empleados[Ciudad]=K5))",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Una ciudad O la otra",
      description: conditions(`En <strong>${ANCHORS[4]}</strong>: muestra los empleados que estén en <strong>cualquiera</strong> de estas ciudades:`, [
        "La ciudad de <strong>K5</strong>",
        "La ciudad de <strong>K6</strong>",
      ]),
      solution: [
        `Selecciona ${F(ANCHORS[4])}.`,
        "Para O se suman las condiciones: si al menos una es 1, el resultado es mayor que 0 y la fila se incluye.",
        `Escribe ${F("=FILTRAR(Empleados,(Empleados[Ciudad]=K5)+(Empleados[Ciudad]=K6))")}.`,
      ],
      expectedResult: "Empleados de Monterrey o Guadalajara",
      finalFormula: "=FILTRAR(Empleados,(Empleados[Ciudad]=K5)+(Empleados[Ciudad]=K6))",
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Solo algunas columnas",
      description:
        `En <strong>${ANCHORS[5]}</strong>: muestra solo <strong>Nombre</strong>, <strong>Departamento</strong> y <strong>Puesto</strong> de los empleados de la ciudad de <strong>K5</strong>. El resultado debe tener exactamente esas <strong>3 columnas</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[5])}.`,
        `La matriz no tiene que ser la tabla completa: ${F("Empleados[[Nombre]:[Puesto]]")} toma desde la columna Nombre hasta Puesto.`,
        `Escribe ${F("=FILTRAR(Empleados[[Nombre]:[Puesto]],Empleados[Ciudad]=K5)")}.`,
      ],
      expectedResult: "3 columnas de los empleados de Monterrey",
      finalFormula: "=FILTRAR(Empleados[[Nombre]:[Puesto]],Empleados[Ciudad]=K5)",
    },
    {
      id: 7,
      level: "intermedio",
      title: "Tarea 7: Puesto que contiene un texto",
      description:
        `En <strong>${ANCHORS[6]}</strong>: muestra los empleados cuyo <strong>Puesto contenga</strong> el texto de <strong>K9</strong> (por ejemplo \"Analista de datos\", \"Analista financiero\"…). FILTRAR no acepta comodines: usa <strong>ESNUMERO(HALLAR(...))</strong>.`,
      solution: [
        `Selecciona ${F(ANCHORS[6])}.`,
        `${F("HALLAR(K9,Empleados[Puesto])")} devuelve la posición del texto en cada puesto o un error si no está. ESNUMERO lo convierte en VERDADERO/FALSO.`,
        `Escribe ${F("=FILTRAR(Empleados,ESNUMERO(HALLAR(K9,Empleados[Puesto])))")}.`,
      ],
      expectedResult: "Todos los analistas",
      finalFormula: "=FILTRAR(Empleados,ESNUMERO(HALLAR(K9,Empleados[Puesto])))",
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: Talento destacado",
      description: conditions(
        `En <strong>${ANCHORS[7]}</strong>: muestra los empleados del departamento de <strong>K4</strong> que, <em>además</em>, cumplan <strong>al menos una</strong> de estas:`,
        ["Evaluación de <strong>4 o más</strong>", "Salario mayor al de <strong>K7</strong>"]
      ),
      solution: [
        `Selecciona ${F(ANCHORS[7])}.`,
        "Combina las dos técnicas: la O va entre paréntesis (suma) y se multiplica por la condición del departamento (Y).",
        `Escribe ${F("=FILTRAR(Empleados,(Empleados[Departamento]=K4)*((Empleados[Evaluación]>=4)+(Empleados[Salario]>K7)))")}.`,
      ],
      expectedResult: "Empleados de Ventas bien evaluados o con salario alto",
      finalFormula:
        "=FILTRAR(Empleados,(Empleados[Departamento]=K4)*((Empleados[Evaluación]>=4)+(Empleados[Salario]>K7)))",
    },
  ],
};
